import React, { useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { fetchMyPackageApi } from '../../api/packages.api';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useI18n } from '../../hooks/useI18n';
import { useOpenSettings } from '../../features/settings/SettingsModalContext';

export const PublishCostConfirmModal = ({ open, onClose, onConfirm, confirming = false }) => {
  const { t, formatNumber } = useI18n();
  const openSettings = useOpenSettings();
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    fetchMyPackageApi()
      .then((value) => {
        if (!cancelled) {
          setEntitlement(value);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEntitlement(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const remaining = entitlement?.publishSlotsRemaining ?? entitlement?.PublishSlotsRemaining ?? 0;
  const max = entitlement?.maxActivePublishes ?? entitlement?.MaxActivePublishes ?? 0;
  const canPublish = remaining > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeDisabled={confirming}
      title={t('publishCostModalTitle')}
      panelClassName="max-w-lg p-0"
      contentClassName="mb-0 gap-2 border-b border-neutral-100 px-4 py-3 dark:border-neutral-900"
      closeButtonClassName="h-8 w-8"
    >
      <div className="space-y-3 px-4 pb-4">
        <p className="text-sm leading-5 text-neutral-500">{t('publishSlotsBody')}</p>
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-3 text-sm dark:border-neutral-800 dark:bg-neutral-900">
          <p>{t('packagePublishSlots', { count: formatNumber(max) })}</p>
          <p className="mt-1 font-semibold">{t('packagePublishRemaining', { count: formatNumber(remaining) })}</p>
        </div>
        {!loading && !canPublish ? (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-2 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
            {t('publishSlotsFull')}
          </p>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={confirming}>
            {t('cancel')}
          </Button>
          {!canPublish && !loading ? (
            <Button type="button" onClick={() => { onClose(); openSettings('packages'); }}>
              {t('upgradePlan')}
            </Button>
          ) : (
            <Button type="button" onClick={onConfirm} disabled={confirming || loading || !canPublish}>
              {confirming || loading ? (
                <>
                  <LoaderCircle size={16} className="animate-spin" />
                  {t('deploymentsPublishingNow')}
                </>
              ) : (
                t('publishCostConfirm')
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
